import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Search as SearchIcon, FileText, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Search({ user, cabinet, onLogout }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [indexStats, setIndexStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("relevance");

  const handleSearch = async () => {
    if (!query.trim()) {
      toast.error("Veuillez entrer une requête");
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('lawliet_token');

    try {
      const response = await axios.post(
        `${API}/documents/search`,
        {
          query: query,
          top_k: 10,
          min_similarity: 0.3
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResults(response.data);
      
      if (response.data.length === 0) {
        toast.info("Aucun résultat trouvé");
      } else {
        toast.success(`${response.data.length} résultat(s) trouvé(s)`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la recherche");
    } finally {
      setLoading(false);
    }
  };

  const loadIndexStats = async () => {
    const token = localStorage.getItem('lawliet_token');
    try {
      const response = await axios.get(`${API}/documents/index/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIndexStats(response.data);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  useEffect(() => {
    loadIndexStats();
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getSimilarityColor = (score) => {
    if (score > 0.7) return "text-green-600";
    if (score > 0.5) return "text-amber-600";
    return "text-slate-600";
  };

  return (
    <Layout user={user} cabinet={cabinet} onLogout={onLogout}>
      <div className="space-y-6" data-testid="search-page">
        <div>
          <h1 className="text-3xl font-bold text-blue-900" style={{ fontFamily: 'EB Garamond, serif' }}>
            Recherche Sémantique
          </h1>
          <p className="text-slate-600 mt-1">Recherchez dans vos documents juridiques avec l'IA</p>
        </div>

        {/* Search stats */}
        {indexStats && (
          <div className="grid md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Documents indexés</p>
                    <p className="text-2xl font-bold text-blue-900">{indexStats.unique_documents}</p>
                  </div>
                  <FileText className="h-8 w-8 text-blue-900 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Chunks vectorisés</p>
                    <p className="text-2xl font-bold text-blue-900">{indexStats.total_vectors}</p>
                  </div>
                  <Sparkles className="h-8 w-8 text-blue-900 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div>
                  <p className="text-sm text-slate-600">Modèle d'embeddings</p>
                  <p className="text-xs font-medium text-blue-900 mt-1">Multilingual MiniLM</p>
                  <Badge variant="outline" className="mt-2">Open Source</Badge>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Search bar */}
        <Card>
          <CardHeader>
            <CardTitle>Rechercher</CardTitle>
            <CardDescription>
              Posez une question ou recherchez des concepts dans vos documents
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex space-x-2">
              <Input
                data-testid="search-input"
                placeholder="Ex: clause de résiliation, responsabilité contractuelle..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1"
              />
              <Button
                data-testid="search-button"
                onClick={handleSearch}
                disabled={loading || !query.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <SearchIcon className="h-4 w-4" />
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {results.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Résultats ({results.length})</CardTitle>
                  <CardDescription>Passages les plus pertinents</CardDescription>
                </div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="search-sort-select"
                >
                  <option value="relevance">Par pertinence</option>
                  <option value="score_desc">Score élevé d'abord</option>
                  <option value="score_asc">Score faible d'abord</option>
                </select>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-4">
                  {[...results].sort((a, b) => {
                    if (sortBy === "score_desc") return b.similarity_score - a.similarity_score;
                    if (sortBy === "score_asc") return a.similarity_score - b.similarity_score;
                    return 0;
                  }).map((result, idx) => (
                    <Card key={idx} className="border-l-4 border-l-blue-500" data-testid="search-result">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <FileText className="h-4 w-4 text-blue-900" />
                            <span className="text-sm font-medium text-slate-700">
                              {result.source_metadata?.filename || `Document ${result.document_id.substring(0, 8)}...`}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`text-sm font-semibold ${getSimilarityColor(result.similarity_score)}`}>
                              {(result.similarity_score * 100).toFixed(1)}% similaire
                            </span>
                            <Badge variant="outline" className="text-xs">
                              Chunk {result.chunk_index}
                            </Badge>
                          </div>
                        </div>
                        <p className="text-slate-800 leading-relaxed whitespace-pre-wrap">
                          {result.chunk_text}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Empty state */}
        {results.length === 0 && query === "" && (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <SearchIcon className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">
                Utilisez la recherche sémantique pour trouver des concepts dans vos documents
              </p>
              <p className="text-sm text-slate-500 mt-2">
                L'IA comprend le sens de votre recherche, pas seulement les mots-clés
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
